/* eslint-disable jsx-a11y/no-static-element-interactions */

import * as React from 'react';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { Filter } from 'react-table';
import {
  EventType,
  MediaPreviewState,
  ParticipantViewModel,
  ParticipantVirtualGraduationState,
  ParticipantVirtualGraduationViewModel,
  UserRole,
} from 'stageclip-common';
import { faEye } from '@fortawesome/free-solid-svg-icons';
import classNames from 'classnames';

import { createModalOpenAction } from '../../../actions/modalActions';
import {
  createAllEventParticipantsRequestedAction,
  createEventArchivedParticipantsRequestedAction,
} from '../../../actions/participantActions';
import * as ParticipantWarnings from '../../../helpers/participantWarningsHelper';
import { ClippingPlatformState } from '../../../store/clippingPlatformState';
import { getAllWithIds, getItem } from '../../../store/selectors';
import DownloadButton from '../../buttons/downloadButton';
import Button from '../../buttons/button';
import {
  BaseDispatchProps,
  baseMapDispatchToProps,
  baseMapStateToProps,
  BaseStateProps,
  ComponentBase,
} from '../../componentBase';
import { AppTable, AppTableColumn } from '../../table/appTable';
import { UnlockClipModal } from '../unlockClipModal/UnlockClipModal';
import { ResetRenderModal } from '../resetRenderModal/ResetRenderModal';

import { SimpleModalId } from 'constants/modalId';
import { ReviewModal } from './reviewModal/ReviewModal';
import { MediaPreviewCell } from './mediaPreviewCell';
import { ParticipantsState } from './participantsState';
import { ParticipantsVirtualGraduationState } from './participantsVirtualGraduationState';
import { DeleteParticipantModal } from './deleteParticipantModal/DeleteParticipantModal';
import { BlacklistConfirmationModal } from './blacklistConfirmationModal/BlacklistConfirmationModal';
import { PreviewMOModalContainer } from './previewMOModal/PreviewMOModal';
import styles from './participantsTable.module.scss';
import { VideoClipPreviewCell } from './videoClipPreviewCell';

interface ParticipantsTableOwnProps {
  eventId: string;
  eventType: EventType;
  filters?: Filter[];
  archived: boolean;
}

interface ParticipantsTableStateProps extends BaseStateProps {
  warningSettings: any;
  participants: ParticipantViewModel[];
}

interface ParticipantsTableDispatchProps extends BaseDispatchProps {
  loadAllParticipants: (c: ParticipantsTable) => void;
  openEditParticipantModal: (
    participant: ParticipantViewModel,
    reloadParticipants: (c: ParticipantsTable) => void,
  ) => void;
  openReviewModal: (participant: ParticipantViewModel) => void;
  openPreviewSlideModal: (participant: ParticipantViewModel) => void;
}

type ParticipantViewModelVirtualGraduation = ParticipantViewModel & {
  // cause virtualGraduation field is union
  virtualGraduation: ParticipantVirtualGraduationViewModel;
};

const formatValue = ({ value, replaceWith = 'n/a' }: { value: unknown; replaceWith?: unknown }) => value || replaceWith;

class ParticipantsTable extends ComponentBase<
  ParticipantsTableOwnProps,
  ParticipantsTableStateProps,
  ParticipantsTableDispatchProps
> {
  private columns: AppTableColumn[] = [
    {
      Header: '#',
      accessor: (p: ParticipantViewModel) => p.eventOrderIndex,
      charactersLength: 8,
      id: 'index',
      width: 50,
    },
    {
      Header: 'Id',
      accessor: (p: ParticipantViewModel) => p.id,
      id: 'id',
      width: 240,
    },
    {
      Cell: (row: any) => this.renderParticipantName(row.original),
      Header: 'Full name',
      accessor: (p: ParticipantViewModel) => p.fullName,
      className: `${styles.name} break-words`,
      id: 'fullName',
      width: 200,
    },
    {
      Cell: (row: any) => <MediaPreviewCell state={row.value.state} link={row.value.link} />,
      Header: 'Audio Preview',
      accessor: (p: ParticipantViewModel) => {
        return {
          link: p.audioPreviewLink,
          state: p.audioPreviewState,
        };
      },
      id: 'audioPreviewState',
      width: 150,
    },
    {
      Cell: (row: any) => <VideoClipPreviewCell state={row.value.state} link={row.value.link} />,
      Header: 'Video Preview',
      accessor: (p: ParticipantViewModel) => {
        return {
          link: p.videoPreviewLink,
          state: p.videoPreviewState,
        };
      },
      id: 'videoPreviewState',
      width: 150,
    },
    {
      Header: 'Subtitle',
      accessor: (p: ParticipantViewModel) => formatValue({ value: p.subtitle }),
      id: 'subtitle',
      width: 200,
    },
    {
      Header: 'Second Subtitle',
      accessor: (p: ParticipantViewModel) => formatValue({ value: p.secondSubtitle }),
      id: 'secondSubtitle',
      width: 200,
    },
    {
      Header: 'Client participant ID',
      accessor: (p: ParticipantViewModel) => formatValue({ value: p.clientParticipantId }),
      id: 'clientParticipantId',
      width: 80,
    },
    {
      Header: 'Marching Order ID',
      accessor: (p: ParticipantViewModel) => formatValue({ value: p.marchingOrderParticipantId }),
      id: 'marchingOrderParticipantId',
      width: 80,
    },
    {
      Header: 'e-mail',
      accessor: (p: ParticipantViewModel) => formatValue({ value: p.email }),
      id: 'email',
      width: 200,
    },
    {
      Cell: (row: any) => <ParticipantsState state={row.value} />,
      Header: 'State',
      accessor: (p: ParticipantViewModel) => p.state,
      id: 'state',
      width: 140,
    },
    {
      Cell: (row: any) => <ParticipantsVirtualGraduationState state={row.value} />,
      Header: 'VG State',
      accessor: ({ virtualGraduation }: ParticipantViewModel) => virtualGraduation && virtualGraduation.state,
      id: 'vgState',
      width: 140,
      show: this.props.eventType === EventType.VirtualGraduation,
    },
    {
      Header: 'Event video',
      accessor: (p: ParticipantViewModel) => formatValue({ value: p.eventVideo && p.eventVideo.name }),
      id: 'eventVideo',
      width: 150,
      show: false,
    },
    {
      Header: 'Video start time',
      accessor: (p: ParticipantViewModel) => formatValue({ value: p.clipStartTimestamp && `0${p.clipStartTimestamp}` }),
      className: 'break-words',
      id: 'videoStartTime',
      width: 65,
      show: false,
    },
    {
      Header: 'Video end time',
      accessor: (p: ParticipantViewModel) => formatValue({ value: p.clipEndTimestamp && `0${p.clipEndTimestamp}` }),
      className: 'break-words',
      id: 'videoEndTime',
      width: 65,
      show: false,
    },
    {
      Header: 'Audio start time',
      accessor: (p: ParticipantViewModel) =>
        formatValue({ value: p.audioStartTimestamp && `0${p.audioStartTimestamp}` }),
      className: 'break-words',
      id: 'audioStartTime',
      width: 65,
      show: false,
    },
    {
      Header: 'Audio end time',
      accessor: (p: ParticipantViewModel) => formatValue({ value: p.audioEndTimestamp && `0${p.audioEndTimestamp}` }),
      className: 'break-words',
      id: 'audioEndTime',
      width: 65,
      show: false,
    },
    {
      Header: 'Woo commerce ID',
      accessor: (p: ParticipantViewModel) => formatValue({ value: p.wooCommerceId }),
      id: 'wooCommerceId',
      width: 60,
      show: false,
    },
    {
      Cell: (row: any) => formatValue({ value: row.value && <DownloadButton url={row.value} /> }),
      Header: 'Woo commerce permalink',
      accessor: (p: ParticipantViewModel) => p.wooCommercePermalink,
      id: 'wooCommercePermalink',
      width: 100,
    },
    {
      Cell: (row: any) => formatValue({ value: row.value && <DownloadButton url={row.value} /> }),
      Header: 'Clip Video URI',
      accessor: (p: ParticipantViewModel) => p.clipVideoUri,
      id: 'clipVideoUri',
      width: 100,
    },
    {
      Cell: (row: any) => formatValue({ value: row.value && <DownloadButton url={row.value} /> }),
      Header: 'Clip Thumbnail URI',
      accessor: (p: ParticipantViewModel) => p.clipThumbnailUri,
      id: 'clipThumbnailUri',
      width: 100,
    },
    {
      Cell: (row: any) => formatValue({ value: row.value && <DownloadButton url={row.value} /> }),
      Header: 'Clip Gif URI',
      accessor: (p: ParticipantViewModel) => p.clipGifUri,
      id: 'clipGifUri',
      width: 100,
    },
    {
      Cell: ({ value }) => formatValue({ value }),
      Header: 'Snapcode Id',
      accessor: (p: ParticipantViewModel) => p.snapcodeId,
      id: 'snapcodeId',
      width: 230,
      show: false,
    },
    {
      Cell: (row: any) => formatValue({ value: row.value && <DownloadButton url={row.value} /> }),
      Header: 'Slide Image',
      accessor: (p: ParticipantViewModel) => p.virtualGraduation?.image,
      id: 'image',
      width: 120,
      show: false,
    },
    {
      Header: 'Slide Message',
      accessor: (p: ParticipantViewModel) => formatValue({ value: p.virtualGraduation?.message }),
      id: 'message',
      width: 120,
      show: false,
    },
    {
      accessor: ({ virtualGraduation }: ParticipantViewModel) =>
        formatValue({ value: virtualGraduation && virtualGraduation.file }),
      id: 'virtualGraduationVideoUrl',
      show: false,
    },
    {
      Cell: ({ original }: { original: ParticipantViewModelVirtualGraduation }) => {
        return formatValue({
          value: original.virtualGraduation?.slidePath && (
            <Button text="Preview" icon={faEye} slimButton onClick={() => this.props.openPreviewSlideModal(original)} />
          ),
        });
      },
      Header: 'MO Slide',
      id: 'slidePath',
      width: 120,
      show: false,
    },
    {
      Cell: ({ original }: { original: ParticipantViewModelVirtualGraduation }) => {
        return formatValue({
          value: original.virtualGraduation?.audioPath && (
            <MediaPreviewCell state={MediaPreviewState.Available} link={original.virtualGraduation?.audioPath} />
          ),
        });
      },
      Header: 'MO Audio',
      id: 'audioPath',
      width: 120,
      show: false,
    },
    {
      Cell: ({ original }: { original: ParticipantViewModel }) => {
        const { openReviewModal } = this.props;

        return (
          <Button
            text="review"
            onClick={() => openReviewModal(original)}
            icon={faEye}
            allCapitals
            slimButton
            disabled={
              !original.virtualGraduation ||
              original.virtualGraduation.state === ParticipantVirtualGraduationState.Rejected
            }
          />
        );
      },
      Header: 'Review virtual graduation',
      width: 100,
      show:
        this.props.eventType === EventType.VirtualGraduation &&
        (this.props.currentUser.role === UserRole.Admin || this.props.currentUser.role === UserRole.Clipper),
    },
    {
      accessor: ({ blacklist }: ParticipantViewModel) => blacklist,
      id: 'blacklist',
      show: false,
    },
  ];

  protected dataLoaded(props: any) {
    return props.participants !== undefined;
  }

  protected async loadData(props: any) {
    await props.loadAllParticipants(this);
  }

  rowProps = (state: any, { row }: { row: { blacklist: boolean } }) => {
    const { blacklist } = row;

    return {
      className: classNames({
        [styles.blacklistRow]: blacklist,
      }),
    };
  };

  protected loggedInRender() {
    return (
      <>
        <AppTable
          columns={this.columns}
          tableName="participants"
          data={this.props.participants}
          defaultSorted={[{ id: 'index', desc: false }]}
          getTrProps={this.rowProps}
          defaultFiltered={this.props.filters}
        />
        <DeleteParticipantModal />
        <ReviewModal />
        <BlacklistConfirmationModal />
        <PreviewMOModalContainer />
        <UnlockClipModal />
        <ResetRenderModal />
      </>
    );
  }

  private renderParticipantName(participant: ParticipantViewModel) {
    const warnings = ParticipantWarnings.getParticipantWarnings(participant, this.props.warningSettings);

    return (
      // eslint-disable-next-line jsx-a11y/click-events-have-key-events
      <span
        className={warnings.length > 0 ? styles.warning : ''}
        onClick={this.getOpenEditParticipantModelCallback(participant)}
      >
        {warnings.length > 0 && '! '}
        {participant.fullName}
        {warnings.length > 0 && (
          <span className={`${styles.participantWarning}`}>
            <ul>
              {warnings.map(text => (
                <li>{text}</li>
              ))}
            </ul>
          </span>
        )}
      </span>
    );
  }

  private getOpenEditParticipantModelCallback(participant: ParticipantViewModel) {
    const callback = () => this.props.openEditParticipantModal(participant, this.props.loadAllParticipants);

    return callback.bind(this);
  }
}

function mapStateToProps(
  state: ClippingPlatformState,
  ownProps: ParticipantsTableOwnProps,
): ParticipantsTableStateProps {
  const event =
    getItem(state.entities.events, ownProps.eventId) || getItem(state.entities.archivedEvents, ownProps.eventId);

  const clipConfiguration = event && event.clipConfiguration ? JSON.parse(event.clipConfiguration) : null;
  const warningSettings = clipConfiguration && clipConfiguration.warnings ? clipConfiguration.warnings : null;

  return {
    ...baseMapStateToProps(state),
    participants: event.participants ? getAllWithIds(state.entities.participants, event.participants) : undefined,
    warningSettings,
  };
}

function mapDispatchToProps(dispatch: Dispatch, ownProps: ParticipantsTableOwnProps): ParticipantsTableDispatchProps {
  return {
    ...baseMapDispatchToProps(dispatch),
    loadAllParticipants: (c: ParticipantsTable) =>
      dispatch(
        ownProps.archived
          ? createEventArchivedParticipantsRequestedAction(ownProps.eventId, c, dispatch)
          : createAllEventParticipantsRequestedAction(ownProps.eventId, c, dispatch),
      ),
    openEditParticipantModal: (participant: ParticipantViewModel, reloadParticipants: (c: ParticipantsTable) => void) =>
      dispatch(createModalOpenAction(SimpleModalId.EditParticipant, { participant, reloadParticipants })),
    openReviewModal: participant => dispatch(createModalOpenAction(SimpleModalId.ParticipantReview, { participant })),
    openPreviewSlideModal: participant => dispatch(createModalOpenAction(SimpleModalId.PreviewSlide, { participant })),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(ParticipantsTable);
